import React, { memo } from 'react';

import { Flex, Form, Tag } from 'antd-v5';
import cn from 'classnames';
import type { DateTime } from 'luxon';
import { MdExpandLess, MdExpandMore } from 'react-icons/md';
import { Button, DatePicker, Text } from 'scouting-ui/lib';

import { luxonViewDateFormat } from '@shared/constants';
import { intl } from '@shared/localization';

import type {
  ClassEntryFormValues,
  ClassRequirement,
  ClassYouth,
  RequirementEntry,
} from '../types';
import { isFutureDate, requirementKey } from '../utils';
import styles from './ClassEntry.module.scss';
import YouthPicker from './YouthPicker';

type DatePickerProps = React.ComponentProps<typeof DatePicker>;

// DatePicker's popup is portaled but its React events still bubble to the row header
const stopPropagation = (event: React.MouseEvent) => event.stopPropagation();

function RequirementDatePicker(
  props: Omit<DatePickerProps, 'value' | 'onChange'> & {
    value?: DateTime | null;
    onChange?: (date: DateTime | null) => void;
    fallback?: DateTime | null;
  },
) {
  const { value, onChange, fallback, ...rest } = props;

  return (
    <DatePicker
      {...rest}
      value={value ?? fallback ?? undefined}
      onChange={date => onChange?.(date ? (date as DateTime) : null)}
    />
  );
}

export function RequirementLabel(props: { requirement: ClassRequirement }) {
  const { requirement } = props;
  const number = requirement.requirementNumber || requirement.listNumber;

  return (
    <>
      {number && (
        <Text font="body-body-3" strong className={styles.requirementNumber}>
          {number}
        </Text>
      )}
      <Text font="body-body-2">
        <span dangerouslySetInnerHTML={{ __html: requirement.name || '' }} />
      </Text>
    </>
  );
}

interface RequirementRowProps {
  requirement: ClassRequirement;
  roster: ClassYouth[];
  disabledReasons: Map<number, string>;
  eligibleCount: number;
  disabled: boolean;
  expanded: boolean;
  onToggleExpanded: (key: string) => void;
}

function RequirementRow(props: RequirementRowProps) {
  const {
    requirement,
    roster,
    disabledReasons,
    eligibleCount,
    disabled,
    expanded,
    onToggleExpanded,
  } = props;

  const form = Form.useFormInstance<ClassEntryFormValues>();
  const key = requirementKey(requirement);
  const entry: RequirementEntry | undefined = Form.useWatch(
    ['requirements', key],
    { form, preserve: true },
  );
  const completionDate: DateTime | null | undefined = Form.useWatch(
    'completionDate',
    { form, preserve: true },
  );

  const toggleExpanded = () => onToggleExpanded(key);

  const overridden = Boolean(entry?.date);
  const youthCount = entry?.youth?.length ?? 0;
  const panelId = `req-${key}-panel`;

  return (
    <div
      className={cn(styles.requirementCard, {
        [styles.requirementCardOverridden]: overridden,
      })}
    >
      <Flex
        wrap
        align="flex-start"
        gap="12px 18px"
        className={styles.requirementSummary}
        onClick={toggleExpanded}
      >
        <Flex gap={12} align="flex-start" className={styles.requirementText}>
          <RequirementLabel requirement={requirement} />
        </Flex>

        <Flex align="center" gap={16} className={styles.requirementDate}>
          <div onClick={stopPropagation}>
            <Form.Item name={['requirements', key, 'date']} noStyle>
              <RequirementDatePicker
                className={styles.datePicker}
                placeholder={intl.formatMessage({
                  id: 'page.mbcClassEntry.completionDate',
                })}
                format={luxonViewDateFormat}
                fallback={completionDate}
                status={overridden ? 'warning' : undefined}
                disabled={disabled}
                disabledDate={isFutureDate}
              />
            </Form.Item>
          </div>
          <Button type="text" aria-expanded={expanded} aria-controls={panelId}>
            <Tag
              bordered={false}
              color={youthCount === 0 ? 'warning' : 'success'}
            >
              {youthCount === 0 ? '0' : `${youthCount}/${eligibleCount}`}
            </Tag>
            {expanded ? <MdExpandLess size={24} /> : <MdExpandMore size={24} />}
          </Button>
        </Flex>
      </Flex>

      {expanded && (
        <div id={panelId} className={styles.youthPanel}>
          <Form.Item name={['requirements', key, 'youth']} noStyle>
            <YouthPicker
              roster={roster}
              disabledReasons={disabledReasons}
              eligibleCount={eligibleCount}
              disabled={disabled}
            />
          </Form.Item>
        </div>
      )}
    </div>
  );
}

export default memo(RequirementRow);
